import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('ose_cart');
      return saved ? JSON.parse(saved) : [];
    } catch (e) { return []; }
  });
  const [clientProfile, setClientProfile] = useState(() => {
    return localStorage.getItem('ose_profile') || 'particulier';
  });
  const [configuratorSession, setConfiguratorSession] = useState(null);

  // Persist on change
  React.useEffect(() => {
    try {
      const toSave = cartItems.map(i => ({
        product_id: i.product_id,
        quantite: i.quantite,
        product: i.product,
        date_location: i.date_location || null,
        date_retour: i.date_retour || null,
        option_livraison: i.option_livraison || 'livraison_reprise',
        notes: i.notes || '',
      }));
      localStorage.setItem('ose_cart', JSON.stringify(toSave));
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
  }, [cartItems]);

  React.useEffect(() => {
    localStorage.setItem('ose_profile', clientProfile);
  }, [clientProfile]);

  // ─── Ajouter un article avec date et option livraison ─────────────────────
  function addItem(product, quantite = 1, options = {}) {
    setCartItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        return prev.map(i => i.product_id === product.id
          ? {
              ...i,
              quantite: i.quantite + quantite,
              // Mettre à jour la date si fournie
              date_location: options.date_location || i.date_location,
              date_retour: options.date_retour || i.date_retour,
              option_livraison: options.option_livraison || i.option_livraison,
              notes: options.notes || i.notes,
            }
          : i
        );
      }
      return [...prev, {
        product_id: product.id,
        product: product,
        quantite,
        date_location: options.date_location || null,
        date_retour: options.date_retour || null,
        option_livraison: options.option_livraison || (product.type === 'location' ? 'livraison_reprise' : null),
        notes: options.notes || '',
        options,
      }];
    });
  }

  // ─── Mettre à jour la date d'un article ───────────────────────────────────
  function updateItemDate(productId, date_location, date_retour) {
    setCartItems(prev => prev.map(i =>
      i.product_id === productId
        ? { ...i, date_location, date_retour }
        : i
    ));
  }

  // ─── Mettre à jour l'option de livraison ─────────────────────────────────
  function updateItemLivraison(productId, option_livraison) {
    setCartItems(prev => prev.map(i =>
      i.product_id === productId ? { ...i, option_livraison } : i
    ));
  }

  function removeItem(productId) {
    setCartItems(prev => prev.filter(i => i.product_id !== productId));
  }

  function updateQuantity(productId, quantite) {
    if (quantite <= 0) { removeItem(productId); return; }
    setCartItems(prev => prev.map(i => i.product_id === productId ? { ...i, quantite } : i));
  }

  function clearCart() {
    setCartItems([]);
  }

  function getTotal() {
    return cartItems.reduce((sum, item) => {
      const tarifs = item.product?.tarifs || {};
      const prix = tarifs[clientProfile] || tarifs.particulier || 0;
      return sum + prix * item.quantite;
    }, 0);
  }

  function getItemCount() {
    return cartItems.reduce((sum, i) => sum + i.quantite, 0);
  }

  // ─── Tracking panier abandonné (appel backend après 2 min d'inactivité) ────
  useEffect(() => {
    if (cartItems.length === 0) return;
    const timer = setTimeout(async () => {
      try {
        const savedEmail = localStorage.getItem('ose_client_email') || '';
        const savedNom   = localStorage.getItem('ose_client_nom') || '';
        if (!savedEmail) return;
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cart_token: localStorage.getItem('ose_cart_token') || `ct-${Date.now()}`,
            client_email: savedEmail,
            client_nom: savedNom,
            produits: cartItems.map(i => ({ nom: i.product?.nom, id: i.product_id })),
            total: cartItems.reduce((s, i) => s + (i.product?.tarifs?.particulier || 0) * i.quantite, 0),
          }),
        });
      } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    }, 2 * 60 * 1000); // 2 minutes d'inactivité
    return () => clearTimeout(timer);
  }, [cartItems]);

  // ─── Vérifier si un item location a une date valide (≥ 7j) ───────────────
  function checkDateValide(item) {
    if (!item.date_location || item.product?.type !== 'location') return true;
    const today = new Date();
    const dateLocation = new Date(item.date_location);
    const diffDays = Math.floor((dateLocation - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 7;
  }

  // ─── Vérifier tous les items location ont une date ≥ 7j ─────────────────
  function getAlertesDateCart() {
    const alertes = [];
    const today = new Date();
    cartItems.forEach(item => {
      if (item.product?.type !== 'location') return;
      if (!item.date_location) {
        alertes.push({ product_nom: item.product?.nom, type: 'no_date' });
        return;
      }
      const dateLocation = new Date(item.date_location);
      const diffDays = Math.floor((dateLocation - today) / (1000 * 60 * 60 * 24));
      if (diffDays < 7) {
        alertes.push({ product_nom: item.product?.nom, type: 'trop_proche', diffDays });
      }
    });
    return alertes;
  }

  const cartContextValue = useMemo(() => ({
    cartItems, setCartItems,
    clientProfile, setClientProfile,
    configuratorSession, setConfiguratorSession,
    addItem, removeItem, updateQuantity, clearCart,
    updateItemDate, updateItemLivraison,
    getTotal, getItemCount,
    checkDateValide, getAlertesDateCart,
  // eslint-disable-next-line
  }), [cartItems, clientProfile, configuratorSession]);

  return (
    <CartContext.Provider value={cartContextValue}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
